import { Component, HostListener, Input, OnDestroy } from '@angular/core';
import { Customer } from '@inyova/models';

import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromStrategy from '@strategy/reducers';

import { MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { StockAllocationitem, StrategyStockAllocation } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';

import { DonutChartConfig } from '../strategy-donut-chart/strategy-donut-chart.component';

@Component({
  selector: 'app-strategy-stock-allocation',
  templateUrl: './strategy-stock-allocation.component.html',
  styleUrls: ['./strategy-stock-allocation.component.scss'],
  standalone: false
})
export class StrategyStockAllocationComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  @Input() customer: Customer;
  activeTab: StrategyStockAllocation['currentTab'];
  stockAllocationSector: StrategyStockAllocation['sector_percentages'];
  stockAllocationCurrency: StrategyStockAllocation['currency_percentages'];
  fullList = [];
  chartInitialised = false;
  shortCurrencyList = [];
  shortSectorList = [];
  shortList = [];
  showMore = true;
  selectedIndex = null;
  selectedRandomCompanies = [];
  donutChartConfig: DonutChartConfig = {
    height: 180,
    width: 180,
    margin: 5,
    radius: 180 / 2,
    thickness: 42,
    grow: 2
  };

  constructor(
    private store: Store,
    private trackingService: TrackingService
  ) {
    this.store.pipe(select(fromStrategy.selectStockAllocation), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value.currency_percentages.length && !value.sector_percentages.length) {
        return;
      }

      this.stockAllocationSector = value.sector_percentages;
      this.stockAllocationCurrency = value.currency_percentages;
      this.shortSectorList = this.getShortList(this.stockAllocationSector);
      this.shortCurrencyList = this.getShortList(this.stockAllocationCurrency);

      if (!this.chartInitialised && this.stockAllocationSector?.length) {
        this.shortList = this.shortSectorList;
        this.fullList = this.stockAllocationSector;
        this.chartInitialised = true;
      }
    });
    this.store.pipe(select(fromStrategy.selectStockAllocationDetails), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!this.activeTab || this.activeTab !== value.currentTab) {
        this.shortList = value.currentTab === 'sector' ? this.shortSectorList : this.shortCurrencyList;
        this.fullList = value.currentTab === 'sector' ? this.stockAllocationSector : this.stockAllocationCurrency;
      }
      this.activeTab = value.currentTab;
      this.selectedIndex = value.selectedIndex;
    });
    this.store.pipe(select(fromStrategy.selectRandomStockCompanies), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.selectedRandomCompanies = value;
    });

    this.showMore = !(window.innerWidth <= MOBILE_VIEW_TRESHOLD);
  }

  getShortList(target: StockAllocationitem[]): StockAllocationitem[] {
    let totalFirstTree = 0;
    const shortList = target.filter((item, index) => {
      if (index < 3) {
        totalFirstTree += Number(item.value);
        return item;
      }
    });

    shortList.push({
      id: 4,
      color: 'var(--chart-other-color)',
      key: 'other',
      value: 1 - totalFirstTree
    });
    return shortList;
  }

  switchTab(tabName: StrategyStockAllocation['currentTab']): void {
    if (this.activeTab === tabName) {
      return;
    }

    this.trackingService.trackActivity(`[Button] Switch stocks allocation tab: ${tabName}`);
    this.store.dispatch(StrategyActions.setStockAllocationDetails({ currentTab: tabName, selectedIndex: null }));
  }

  switchShowMore(): void {
    this.showMore = !this.showMore;
    this.trackingService.trackActivity(`[Button] Stocks allocation shortlist - Show ${this.showMore ? 'more' : 'less'}`);
  }

  setSelected(index: number): void {
    this.trackingService.trackActivity(`[Button] ${this.selectedIndex === index ? 'Unselect' : 'Select'} stocks allocation item`);

    if (this.selectedIndex === index) {
      this.store.dispatch(StrategyActions.setStockAllocationDetails({ currentTab: this.activeTab, selectedIndex: null }));
      return;
    }

    this.store.dispatch(StrategyActions.setStockAllocationDetails({ currentTab: this.activeTab, selectedIndex: index }));
  }

  @HostListener('window:resize')
  onResize(): void {
    this.showMore = !(window.innerWidth <= MOBILE_VIEW_TRESHOLD);
    this.store.dispatch(StrategyActions.setStockAllocationDetails({ currentTab: this.activeTab, selectedIndex: null }));
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
